/**
 * Entryway tiles (the one card grid left: inside the Hubs the left rail and
 * lists took over, Jon 2026-11-03), plus the light Panel used by Home and the
 * Knowledgebase. Shape follows PLC Home's entryway (docs/reference/plc-home-entryway):
 * frosted white card, big rounded corners, centred icon / title / line / button,
 * a muted "coming soon" state. Colours are DWA tokens.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { DWA } from '../theme/dwaTokens';
import type { Hub } from './hubs';

const cardShell: React.CSSProperties = {
  background: DWA.cardBg,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: DWA.radius,
  border: '1px solid rgba(255,255,255,0.3)',
  boxShadow: DWA.cardShadow,
};

export const ComingSoonPill: React.FC = () => (
  <span
    style={{
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#7a4b00',
      background: '#fff1d6',
      border: '1px solid #f8d79a',
      borderRadius: '999px',
      padding: '0.15rem 0.55rem',
      whiteSpace: 'nowrap',
    }}
  >
    Coming soon
  </span>
);

const IconTile: React.FC<{ icon: IconDefinition; muted?: boolean; size?: number }> = ({ icon, muted, size = 72 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: DWA.radius,
      background: muted ? 'rgba(120,120,120,0.12)' : `linear-gradient(135deg, ${DWA.dark}, ${DWA.darkSoft})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <FontAwesomeIcon icon={icon} style={{ color: muted ? '#9ca3af' : DWA.gold, fontSize: size * 0.4 }} />
  </div>
);

/** Entryway tile for one Hub. */
export const HubTile: React.FC<{ hub: Hub }> = ({ hub }) => {
  const comingSoon = hub.status === 'coming-soon';
  return (
    <Link
      to={hub.route}
      className="hub-card-link"
      data-testid={`hub-tile-${hub.id}`}
      style={{
        ...cardShell,
        padding: '1.75rem 1.25rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.75rem',
        textAlign: 'center',
        textDecoration: 'none',
        color: DWA.ink,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        opacity: comingSoon ? 0.85 : 1,
      }}
    >
      <IconTile icon={hub.icon} muted={comingSoon} />
      <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>{hub.title}</h2>
      <p style={{ fontSize: '0.85rem', color: DWA.textSecondary, margin: 0, lineHeight: 1.5 }}>{hub.description}</p>
      <div style={{ marginTop: 'auto', paddingTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {comingSoon && <ComingSoonPill />}
        <span
          className="hub-gold-button"
          style={{
            padding: '0.5rem 1.4rem',
            backgroundColor: DWA.gold,
            color: DWA.onGold,
            borderRadius: DWA.radius,
            fontSize: '0.85rem',
            fontWeight: 700,
          }}
        >
          Open
        </span>
      </div>
    </Link>
  );
};

/** Panel on the light page (Home, Knowledgebase): white card, icon + title, optional action. */
export const Panel: React.FC<{
  icon: IconDefinition;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, action, children }) => (
  <section
    style={{
      background: '#ffffff',
      border: `1px solid ${DWA.lineLight}`,
      borderRadius: DWA.radius,
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      padding: '1.1rem 1.15rem',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      color: DWA.ink,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.85rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: DWA.radius,
            background: DWA.dark,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={icon} style={{ color: DWA.gold, fontSize: '0.8rem' }} />
        </span>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: DWA.ink, margin: 0 }}>{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

/** Light page body inside the Hubs shell (data screens). */
export const LightPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ flex: 1, background: DWA.pageBg, fontFamily: DWA.font, color: DWA.ink }}>{children}</div>
);

/**
 * Dark page body (login-screen background): entryway, Home, Hub pages, Knowledgebase.
 * `flex: 1` fills the page column. No `minHeight: 100%`: under the breadcrumb strip that
 * made the page 40px taller than the window, so #root scrolled and showed a light strip
 * under the left rail (Jon, 2026-11-03).
 */
export const GradientPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      flex: 1,
      background: DWA.entrywayBackground,
      fontFamily: DWA.font,
      color: DWA.onDark,
    }}
  >
    {children}
  </div>
);
